/**
 * Resolves which ticket an exchange is being made against.
 *
 * The ticket list dispatches `selectedPassengerToAlter` as `{ ticketKey, nodeTripName }` — a key
 * and the leg it was picked from — and everything downstream has to turn that pair back into the
 * ticket itself. It is one answer, so it lives in one place: the schedule screen's summary and the
 * legacy original-ticket card resolve it identically, and a screen that shows the wrong ticket
 * cannot disagree with the screen that chose it.
 *
 * A tenant whose flow never picks a ticket (there is only one) falls back to the first of the
 * exchange's own passengers, which is what the flow has always shown.
 *
 * @param {object} exchange - The `exchange` slice, or the parts of it named below.
 * @param {object} [exchange.selectedPassengerToAlter] - `{ ticketKey, nodeTripName }` as dispatched.
 * @param {object} [exchange.allPassengers] - Tickets by leg, keyed `depart` / `return`.
 * @param {Array<object>} [exchange.passengers] - The exchange's own passengers.
 * @returns {{ticket: object|undefined, nodeTripName: string, isReturnTrip: boolean, isPicked:
 *   boolean}} The ticket being changed and the leg it belongs to. `nodeTripName` defaults to
 *   `depart`, the leg the fallback ticket comes from. `isPicked` says whether the traveller chose
 *   this ticket, which is what scopes the exchange to a single leg.
 */
const resolveSelectedExchangeTicket = ({
  selectedPassengerToAlter,
  allPassengers,
  passengers,
} = {}) => {
  const ticketKey = selectedPassengerToAlter?.ticketKey;
  const nodeTripName = (ticketKey && selectedPassengerToAlter?.nodeTripName) || 'depart';

  const ticket = ticketKey
    ? allPassengers?.[nodeTripName]?.find((passenger) => passenger.transporterKey === ticketKey)
    : passengers?.[0];

  return {
    ticket,
    nodeTripName,
    isReturnTrip: nodeTripName === 'return',
    isPicked: Boolean(ticketKey),
  };
};

export default resolveSelectedExchangeTicket;
